import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Lightbulb, Loader2, Download, Search, RefreshCw, Trash2, MessageSquare,
  CheckCircle2, Clock, Wrench, Archive, Inbox, X,
} from 'lucide-react';
import IdeaSpotlightPanel from '../../../components/admin/IdeaSpotlightPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  new: { label: 'New', cls: 'bg-blue-100 text-blue-800 border-blue-200', Icon: Inbox },
  reviewed: { label: 'Reviewed', cls: 'bg-purple-100 text-purple-800 border-purple-200', Icon: CheckCircle2 },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-800 border-amber-200', Icon: Clock },
  implemented: { label: 'Implemented', cls: 'bg-green-100 text-green-800 border-green-200', Icon: Wrench },
  archived: { label: 'Archived', cls: 'bg-gray-100 text-gray-700 border-gray-200', Icon: Archive },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return '—'; }
};
const fmtFull = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return ''; }
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.new;
  const Ico = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${meta.cls}`}>
      <Ico className="w-3 h-3" /> {meta.label}
    </span>
  );
};

/* ===========================================================================
 * Detail drawer — opened when a row is clicked
 * ===========================================================================*/
const DetailDrawer = ({ item, typeLabels, onClose, onUpdate, onDelete }) => {
  const [notes, setNotes] = useState(item?.admin_notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setNotes(item?.admin_notes || ''); }, [item]);

  if (!item) return null;
  const saveNotes = async () => {
    setSaving(true);
    try {
      await onUpdate(item.id, { admin_notes: notes });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose} data-testid="suggestion-drawer">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB] sticky top-0 bg-white">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Lightbulb className="w-5 h-5 text-[#BA1826]" /> Suggestion Detail</h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="suggestion-drawer-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <StatusPill status={item.status} />
            <span className="text-[10px] font-bold uppercase tracking-wider bg-[#F1F5F9] text-[#4B5563] border border-[#E5E7EB] px-1.5 py-0.5 rounded">
              {typeLabels[item.suggestion_type] || item.suggestion_type}
            </span>
          </div>

          <div className="text-xs text-[#4B5563] space-y-1">
            <p><strong>From:</strong> {item.name || <em className="text-[#9CA3AF]">Anonymous</em>}</p>
            {item.email && <p><strong>Email:</strong> <a href={`mailto:${item.email}`} className="text-[#BA1826] hover:underline">{item.email}</a></p>}
            <p><strong>Submitted:</strong> {fmtFull(item.created_at)}</p>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">Message</p>
            <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded p-3 text-sm whitespace-pre-wrap" data-testid="suggestion-drawer-message">{item.message}</div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-2">Change status</p>
            <div className="grid grid-cols-2 gap-1.5" data-testid="suggestion-status-buttons">
              {Object.entries(STATUS_META).map(([k, m]) => (
                <button key={k} onClick={() => onUpdate(item.id, { status: k })}
                  className={`text-xs font-semibold py-2 px-2 rounded border transition-all ${
                    item.status === k ? `${m.cls} ring-2 ring-offset-1 ring-[#BA1826]` : 'bg-white border-[#E5E7EB] text-[#4B5563] hover:border-[#BA1826]'
                  }`} data-testid={`set-status-${k}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">Admin notes</p>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} maxLength={2000}
              placeholder="Internal notes for the team (not visible to the submitter)..."
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none resize-none"
              data-testid="suggestion-notes-input" />
            <div className="flex justify-end mt-2">
              <button onClick={saveNotes} disabled={saving || notes === (item.admin_notes || '')}
                className="text-xs font-semibold bg-[#0A0A0A] text-white px-3 py-1.5 rounded hover:bg-[#333] disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="suggestion-save-notes-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Save notes
              </button>
            </div>
          </div>

          {(item.status_history && item.status_history.length > 0) && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">History</p>
              <ul className="text-[11px] text-[#4B5563] space-y-0.5">
                {item.status_history.map((h, i) => (
                  <li key={i}>
                    <strong>{h.from}</strong> → <strong>{h.to}</strong> by {h.by} · {fmtDate(h.at)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.status === 'implemented' && (
            <IdeaSpotlightPanel suggestionId={item.id} />
          )}

          <div className="pt-3 border-t border-[#E5E7EB]">
            <button onClick={() => { if (window.confirm('Delete this suggestion permanently?')) onDelete(item.id); }}
              className="text-xs font-semibold text-red-600 hover:text-red-800 inline-flex items-center gap-1"
              data-testid="suggestion-delete-btn">
              <Trash2 className="w-3.5 h-3.5" /> Delete suggestion
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ===========================================================================
 * Main admin tab
 * ===========================================================================*/
const AdminSuggestionsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ status: '', suggestion_type: '', search: '' });
  const [selected, setSelected] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await axios.get(`${BACKEND_URL}/api/admin/suggestions?${params}`, { withCredentials: true });
      setData(r.data); setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load suggestions');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUpdate = async (id, body) => {
    await axios.put(`${BACKEND_URL}/api/admin/suggestions/${id}`, body, { withCredentials: true });
    // Optimistic local update + refresh
    setData(prev => prev ? ({
      ...prev,
      rows: prev.rows.map(r => r.id === id ? { ...r, ...body } : r),
    }) : prev);
    if (selected?.id === id) setSelected(s => ({ ...s, ...body }));
    fetchAll();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${BACKEND_URL}/api/admin/suggestions/${id}`, { withCredentials: true });
    setSelected(null);
    fetchAll();
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.suggestion_type) params.set('suggestion_type', filters.suggestion_type);
    window.location.href = `${BACKEND_URL}/api/admin/suggestions/export.csv?${params}`;
  };

  const counts = data?.counts || {};
  const rows = data?.rows || [];
  const typeLabels = data?.type_labels || {};

  return (
    <div className="space-y-5" data-testid="admin-suggestions-tab">
      <div>
        <h2 className="font-heading text-lg font-bold text-[#0A0A0A] flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-[#BA1826]" />
          Suggestions & Feedback
        </h2>
        <p className="text-sm text-[#4B5563] mt-1">
          Community ideas submitted via the homepage form. Click any row to review, change status, or add internal notes.
        </p>
      </div>

      {/* Status counters */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2" data-testid="suggestions-counters">
        <button onClick={() => setFilters({ ...filters, status: '' })}
          className={`text-left p-3 rounded border ${!filters.status ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white border-[#E5E7EB] hover:border-[#0A0A0A]'}`}
          data-testid="counter-all">
          <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">All</p>
          <p className="text-xl font-heading font-bold">{counts.total || 0}</p>
        </button>
        {Object.entries(STATUS_META).map(([k, m]) => {
          const Ico = m.Icon;
          const active = filters.status === k;
          return (
            <button key={k} onClick={() => setFilters({ ...filters, status: active ? '' : k })}
              className={`text-left p-3 rounded border ${active ? `${m.cls} ring-2 ring-offset-1 ring-[#BA1826]` : 'bg-white border-[#E5E7EB] hover:border-[#BA1826]'}`}
              data-testid={`counter-${k}`}>
              <p className="text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1"><Ico className="w-3 h-3" /> {m.label}</p>
              <p className="text-xl font-heading font-bold">{counts[k] || 0}</p>
            </button>
          );
        })}
      </div>

      {/* Filters & actions */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input type="text" placeholder="Search name, email, or message..." value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="w-full pl-8 pr-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
            data-testid="suggestions-search-input" />
        </div>
        <select value={filters.suggestion_type} onChange={(e) => setFilters({ ...filters, suggestion_type: e.target.value })}
          className="px-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
          data-testid="suggestions-type-filter">
          <option value="">All Types</option>
          {Object.entries(typeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={fetchAll} disabled={loading}
          className="text-xs px-3 py-2 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA] flex items-center gap-1 disabled:opacity-50"
          data-testid="suggestions-refresh-btn">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <button onClick={exportCsv}
          className="text-xs px-3 py-2 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA] flex items-center gap-1"
          data-testid="suggestions-export-btn">
          <Download className="w-3 h-3" /> Export CSV
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">{error}</div>}

      {/* List */}
      {rows.length === 0 ? (
        <div className="text-center py-10 text-[#9CA3AF] text-sm" data-testid="suggestions-empty">
          {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> :
            'No suggestions match your filters. As community members submit ideas via the homepage, they\'ll appear here.'}
        </div>
      ) : (
        <div className="border border-[#E5E7EB] rounded overflow-x-auto" data-testid="suggestions-list">
          <table className="w-full text-xs">
            <thead className="bg-[#F8F9FA] border-b border-[#E5E7EB]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                <th className="px-3 py-2 font-bold">Status</th>
                <th className="px-3 py-2 font-bold">Type</th>
                <th className="px-3 py-2 font-bold">From</th>
                <th className="px-3 py-2 font-bold">Message</th>
                <th className="px-3 py-2 font-bold">Submitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {rows.map(r => (
                <tr key={r.id} onClick={() => setSelected(r)}
                  className="cursor-pointer hover:bg-[#F8F9FA]" data-testid={`suggestion-row-${r.id}`}>
                  <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                  <td className="px-3 py-2 text-[#4B5563]">{typeLabels[r.suggestion_type] || r.suggestion_type}</td>
                  <td className="px-3 py-2 text-[#0A0A0A] font-medium max-w-[140px] truncate">
                    {r.name || <em className="text-[#9CA3AF]">Anonymous</em>}
                  </td>
                  <td className="px-3 py-2 text-[#4B5563] max-w-md truncate">
                    {r.message}
                    {r.email && <span className="text-[10px] text-[#9CA3AF] ml-1.5">· {r.email}</span>}
                  </td>
                  <td className="px-3 py-2 text-[#4B5563] whitespace-nowrap" title={fmtFull(r.created_at)}>{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[10px] text-[#9CA3AF]">{rows.length} suggestion{rows.length === 1 ? '' : 's'} shown</p>

      <DetailDrawer item={selected} typeLabels={typeLabels} onClose={() => setSelected(null)}
        onUpdate={handleUpdate} onDelete={handleDelete} />
    </div>
  );
};

export default AdminSuggestionsTab;
